import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useService } from '../providers/ServiceProvider';
import { Test } from '@analyzer/client';

export function useTestListQuery(projectId: string, queryParams?: { limit?: number; offset?: number }) {
  const client = useService();
  return useQuery<Test[], Error>(
    ['test-list', projectId, queryParams],
    () => client.testList(projectId, queryParams),
    {
      keepPreviousData: true
    }
  );
}

export function useTestCreateMutation() {
  const client = useService();
  const queryClient = useQueryClient();
  return useMutation(
    (data: {
      projectId: string;
      test: { name: string; target: string; actions?: string; initScript?: string };
    }) => client.testCreate(data.projectId, data.test),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['test-list']);
      }
    }
  );
}

export function useTestReadQuery(projectId: string, testId: string | null) {
  const client = useService();
  return useQuery<Test, Error>({
    queryKey: ['test-read', testId],
    queryFn: () => client.testRead(projectId, testId!),
    enabled: !!testId
  });
}

export function useTestUpdateMutation() {
  const client = useService();
  const queryClient = useQueryClient();
  return useMutation(
    (data: {
      projectId: string;
      testId: string;
      test: { name?: string; target?: string; actions?: string; initScript?: string; enabled?: boolean };
    }) => client.testUpdate(data.projectId, data.testId, data.test),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['test-list']);
      }
    }
  );
}

export function useTestDestroyMutation() {
  const client = useService();
  const queryClient = useQueryClient();
  return useMutation(
    (data: { projectId: string; testId: string }) => client.testDestroy(data.projectId, data.testId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['test-list']);
      }
    }
  );
}
